import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Spinner, Button, Card } from 'react-bootstrap';
import { GrGallery, GrDocumentText, GrCatalogOption, GrImage } from "react-icons/gr";
import { get } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import ArticleEditor from './editors/ArticleEditor';
import CatalogEditor from './editors/CatalogEditor';
import GalleryEditor from "./editors/GalleryEditor";
import GalleryItemEditor from "./editors/GalleryItemEditor";
import PosterEditor from "./editors/PosterEditor";
import Loading from "./editors/Loading";

function EntityEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = !!pageId;

  const typeParam = (searchParams.get('type') || '').toLowerCase().replace('-','');
  const [resolvedType, setResolvedType] = useState(typeParam || '');
  const [checking, setChecking] = useState(isEdit);

  useEffect(() => {
    let mounted = true;
    async function detectType() {
      if (!isEdit) return;
      try {
        setChecking(true);
        const resp = await get(`pages/${pageId}`);
        const type = resp?.type;
        if (!mounted) return;
        setResolvedType((type || '').toLowerCase());
      } catch (e) {
        if (mounted) toast.error('Failed to load page data.');
      } finally {
        if (mounted) setChecking(false);
      }
    }
    detectType();
    return () => { mounted = false; };
  }, [id, pageId]);

  // keep resolvedType in sync with query when creating
  useEffect(() => {
    if (!isEdit) {
      setResolvedType((typeParam || ''));
    }
  }, [typeParam, isEdit]);

  const EditorComponent = useMemo(() => {
    switch ((resolvedType || '').toLowerCase()) {
      case 'article':
        return ArticleEditor;
      case 'catalog':
        return CatalogEditor;
      case 'gallery':
        return GalleryEditor;
      case 'galleryitem':
        return GalleryItemEditor;
      case 'poster':
        return PosterEditor;
      default:
        return Loading;
    }
  }, [resolvedType]);

  if (checking) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  // If creating and no type selected yet, show chooser
  if (!isEdit && !resolvedType) {
    const choose = (t) => {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('type', t);
        return p;
      });
    };

    const options = [
      {
        id: 'article',
        title: 'Article',
        icon: <GrDocumentText size={24} />,
        description: 'A simple page with text and images, perfect for blog posts or news.'
      },
      {
        id: 'catalog',
        title: 'Catalog',
        icon: <GrCatalogOption size={24} />,
        description: 'A list of items with details, suitable for products or services. Catalog may contain other catalogs to create a hierarchy and/or other kinds of pages.'
      },
      {
        id: 'gallery',
        title: 'Gallery',
        icon: <GrGallery size={24} />,
        description: 'A collection of images with captions, ideal for portfolios or photo albums.'
      },
      {
        id: 'poster',
        title: 'Poster',
        icon: <GrImage size={24} />,
        description: 'A full-screen hero section with an image or video background and 1–2 headings, typically used on the main page.'
      }
    ];

    return (
      <div className="container mt-4">
        <div className="row">
          <div className="col">
            <Card>
              <Card.Body>
                <div className="structure-node-header mb-3">
                  <h2 className="m-0">Choose what to add</h2>
                </div>
                <div className="entity-chooser-list">
                  {options.map(opt => (
                    <div
                      key={opt.id}
                      className="structure-node entity-chooser-item"
                      onClick={() => choose(opt.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && choose(opt.id)}
                    >
                      <div className="structure-node-header entity-chooser-header">
                        {opt.icon}
                        {opt.title}
                      </div>
                      <div className="structure-node-description">{opt.description}</div>
                    </div>
                  ))}
                  <div className="mt-3">
                    <Button className="primary-button m-0" onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return <EditorComponent />;
}

export default EntityEditor;
